import { API_BASE_URL, API_ENDPOINTS } from "../constants/api";
import Http from "../services/http";

interface AdSsoVerifyResponse {
  is_authenticated: boolean;
  token_expiry: string; // ISO 8601 timestamp, e.g. "2026-09-17T14:05:25Z"
}

export const useAdssoLoginGuard = () => {
  const redirect_uri = `${API_BASE_URL}${API_ENDPOINTS.ADSSO_LOGIN_GUARD}`;

  const ssoLoginRedirect = () => {
    const nonce =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    // Set nonce cookie for backend validation
    document.cookie = `AD_NONCE=${nonce}; Domain=.${new URL(API_BASE_URL || "").hostname}; Path=/; Secure; Max-Age=300`;
    window.location.href = `${process.env.REACT_APP_AD_SSO_BASE_URL}${process.env.REACT_APP_AD_SSO_AUTHORIZE_URL}?client_id=${process.env.REACT_APP_AD_SSO_CLIENT_ID}&scope=openid+profile&response_type=code+id_token&response_mode=form_post&redirect_uri=${encodeURIComponent(redirect_uri)}&nonce=${nonce}`;
  };

  /**
   * Calls the ADSSO verify endpoint to check if the user has a valid AD_ATKN cookie.
   * The AD_ATKN cookie is HttpOnly, so it cannot be read by JavaScript.
   * The verify endpoint reads the cookie server-side and returns { is_authenticated, token_expiry }.
   * Uses Http.get() service which is configured to:
   * - Bypass maintenance guard (auth must work regardless of maintenance state)
   * - Include credentials: "include" for HttpOnly cookie sending
   *
   * @returns true if the user is authenticated, false otherwise (including errors)
   */
  const verifyAdssoToken = async (): Promise<boolean> => {
    try {
      const url = `${API_BASE_URL}${API_ENDPOINTS.ADSSO_LOGIN_VERIFY}`;

      console.log(`ADSSO verify: calling ${API_ENDPOINTS.ADSSO_LOGIN_VERIFY}`);

      // Use Http.get() which is configured to:
      // 1. Bypass maintenance guard for ADSSO_LOGIN_VERIFY
      // 2. Include credentials: "include" for HttpOnly cookies
      // 3. Skip rcode error handling (uses standard HTTP response)
      const data: AdSsoVerifyResponse = await Http.post(url);

      console.log("ADSSO verify response:", {
        is_authenticated: data.is_authenticated,
        token_expiry: data.token_expiry,
      });

      return data.is_authenticated === true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error("ADSSO verify failed:", errorMsg || "(no error message)");
      console.error("ADSSO verify error details:", error);
      return false;
    }
  };

  return { ssoLoginRedirect, verifyAdssoToken };
};
